/**
 * Conteúdo do laudo "Triagem Eletrônica" a partir do JSON "Triagem Digital" gravado em
 * `clinical.triagem_digital`. Reproduz o que o PDF do robô tkk-apa-digital mostra
 * (BusinessProcess/Pdf.py, definir_valores e preencher_json), para a tela e o nosso PDF
 * dizerem o mesmo que o documento que vai para o D2I.
 */

const NIVEIS = ["Nenhum", "Muito Baixo", "Baixo", "Intermediário", "Alto", "Muito Alto"];

function simNao(valor) {
  if ([true, "True", "true", "Sim", "sim"].includes(valor)) return "Sim";
  if ([false, "False", "false", "Não", "nao", "não"].includes(valor)) return "Não";
  return valor;
}

function presentes(grupo) {
  return Object.entries(grupo || {})
    .filter(([, v]) => v && v["Presente?"])
    .map(([nome, v]) => ({ nome, risco: v.Risco || "Nenhum" }));
}

function maiorNivel(grupo) {
  return Object.values(grupo || {}).reduce((maior, v) => {
    const nivel = NIVEIS.indexOf(v && v.Risco);
    return nivel > NIVEIS.indexOf(maior) ? v.Risco : maior;
  }, "Nenhum");
}

// Mesmas regras de preencher_json, inclusive as esquisitas: o tabagismo tem status em texto
// ("Fumante"), então não sobe "Hábitos sociais"; e "Gestante" só some para quem não é do
// sexo feminino e não marcou gravidez.
//
// Idade e IMC saem prontos do laudo: recalcular os limiares aqui faria a tela discordar do
// documento que foi para o D2I no dia em que um deles mudasse.
function estratificacao(t) {
  const sexoGravidez = t["Sexo/Gravidez"] || {};
  const condicoes = t["Condições Clínicas"] || {};
  const habitos = t["Hábitos Sociais"] || {};
  const linhas = [
    { nome: "Idade", valor: (t.Idade || {}).Risco || "Nenhum" },
    { nome: "IMC", valor: (t["Peso/Altura"] || {}).Risco || "Nenhum" },
  ];
  if (sexoGravidez["Possibilidade Gravidez?"]) linhas.push({ nome: "Gestante", valor: "Intermediário" });
  else if (sexoGravidez.Sexo === "Feminino") linhas.push({ nome: "Gestante", valor: "Nenhum" });
  linhas.push(
    { nome: "Condições cardíacas", valor: maiorNivel(condicoes["Cardio-Circulatório"]) },
    { nome: "Condições pulmonares/respiratórias", valor: maiorNivel(condicoes.Pulmonar) },
    { nome: "Condições endócrinas", valor: maiorNivel(condicoes["Endócrino"]) },
    { nome: "Outras condições", valor: maiorNivel(condicoes["Outras Condições"]) }
  );
  if (Object.values(habitos).some((h) => h && [true, "Sim", "true"].includes(h.Status))) {
    linhas.push({ nome: "Hábitos sociais", valor: "Intermediário" });
  }
  linhas.push({ nome: "Risco paciente", valor: t["Risco do Paciente"] || "" });
  return linhas;
}

export function laudoDaTriagem(clinical) {
  const t = clinical && clinical.triagem_digital && clinical.triagem_digital["Triagem Digital"];
  if (!t) return null;
  const pesoAltura = t["Peso/Altura"] || {};
  const sexoGravidez = t["Sexo/Gravidez"] || {};
  const condicoes = t["Condições Clínicas"] || {};
  const habitos = t["Hábitos Sociais"] || {};
  const tabagismo = habitos.Tabagismo || {};
  const historico = t["Histórico de Cirurgias"] || {};
  const ansiedade = t["Ansiedade/Desejo de Informação"] || {};
  const alergias = t["Alergias anteriores"];
  const tiposAnestesia = historico["Tipo de Anestesia"];
  const estratos = estratificacao(t);
  return {
    riscoPaciente: t["Risco do Paciente"] || "",
    asa: t.ASA || "",
    sexo: sexoGravidez.Sexo || "",
    alturaPeso: `${pesoAltura.Altura ?? ""} m – ${pesoAltura.Peso ?? ""} kg`,
    idade: `${(t.Idade && t.Idade.Idade) ?? ""} anos`,
    cirurgiao: t["Cirurgião"] || "",
    telefone: t.WhatsApp || "",
    gravidez: sexoGravidez["Possibilidade Gravidez?"] ? "Sim" : "Não",
    imc: `${pesoAltura.IMC ?? ""} - ${pesoAltura["Classificação"] ?? ""}`,
    riscoImc: pesoAltura.Risco || "Nenhum",
    habitos: {
      etilismo: simNao((habitos.Etilismo || {}).Status),
      tabagismo: tabagismo.Status || "",
      periodo: tabagismo.Periodo || "",
      drogas: simNao((habitos["Outras Drogas"] || {}).Status),
    },
    grupos: [
      { titulo: "Condições cardíacas", itens: presentes(condicoes["Cardio-Circulatório"]) },
      { titulo: "Condições pulmonares/respiratórias", itens: presentes(condicoes.Pulmonar) },
      { titulo: "Condições endócrinas", itens: presentes(condicoes["Endócrino"]) },
      { titulo: "Outras condições", itens: presentes(condicoes["Outras Condições"]) },
    ],
    medicamentos: (t["Uso Medicamentos"] || []).map((m) => ({
      nome: m.Medicamento,
      suspensao: typeof m["Intervalo (dias)"] === "number" ? `${m["Intervalo (dias)"]} dias` : m["Intervalo (dias)"],
    })),
    alergias: Array.isArray(alergias) ? alergias.filter(Boolean) : (alergias ? [alergias] : []),
    tiposAnestesia: Array.isArray(tiposAnestesia) ? tiposAnestesia.filter((tipo) => tipo != null) : [],
    sentimentos: ansiedade["Sentimentos sobre anestesia"] || "",
    desejoDetalhes: ansiedade["Desejo de detalhes?"] || "",
    desejoConsulta: t["Desejo de Consulta?"] || "",
    estratificacao: estratos,
  };
}
